import React from "react";
import { Link } from "react-router-dom";
import { Users, MessageCircle, UsersRound, Plus } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { ZODIAC_GLYPH, avatarSrc } from "./chatUtils";

const Badge = ({ n, testId, prefix = "", className }) => n > 0 && (
  <span data-testid={testId} className={`min-w-[16px] h-[16px] px-1 rounded-full bg-[#C8A96E] text-[#15111A] text-[9px] font-bold flex items-center justify-center ${className}`}>
    {prefix}{n > 9 ? "9+" : n}
  </span>
);

function Tabs({ tab, setTab, mentionTotal }) {
  const cls = (k) => `flex-1 text-[10px] uppercase tracking-[0.15em] py-2 ${tab === k ? "text-[#C8A96E] border-b border-[#C8A96E]" : "text-[#A89C98]"}`;
  return (
    <div className="flex gap-1 mb-4">
      <button data-testid="tab-hubs" onClick={() => setTab("hubs")} className={cls("hubs")}>
        <Users size={12} className="inline mr-1" /> Hubs
      </button>
      <button data-testid="tab-groups" onClick={() => setTab("groups")} className={`${cls("groups")} relative`}>
        <UsersRound size={12} className="inline mr-1" /> Groups
        <Badge n={mentionTotal} testId="tab-groups-badge" className="absolute -top-1 right-1" />
      </button>
      <button data-testid="tab-dms" onClick={() => setTab("dms")} className={cls("dms")}>
        <MessageCircle size={12} className="inline mr-1" /> DMs
      </button>
    </div>
  );
}

function GroupList({ groups, mentions, activeId, onCreate }) {
  return (
    <>
      <Button data-testid="new-group-btn" onClick={onCreate} className="w-full mb-3 bg-[#C8A96E] text-[#15111A] hover:bg-[#A98B55] rounded-none h-9 text-xs uppercase tracking-widest flex items-center gap-1.5 justify-center">
        <Plus size={13} /> New Group
      </Button>
      {groups.length === 0 ? (
        <div className="text-xs text-[#A89C98] px-2" data-testid="groups-empty">No groups yet — create one to travel together.</div>
      ) : groups.map((g) => (
        <Link
          key={g.group_id}
          to={`/chat/group/${g.group_id}`}
          data-testid={`group-item-${g.group_id}`}
          className={`flex items-center gap-3 px-3 py-2 rounded-md mb-1 border-l-2 relative ${activeId === g.group_id ? "bg-[#4A3356]/40 border-l-[#C8A96E]" : "hover:bg-white/5 border-l-transparent"}`}
        >
          {g.avatar ? (
            <img src={avatarSrc(g.avatar)} alt="" data-testid={`group-avatar-${g.group_id}`} className="w-9 h-9 rounded-full object-cover border border-[#C8A96E]/40 shrink-0" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-[#4A3356]/50 border border-white/10 flex items-center justify-center shrink-0">
              <UsersRound size={13} className="text-[#C8A96E]" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-sm text-white truncate font-heading pr-6">{g.name}</div>
            <div className="text-[10px] text-[#A89C98] mt-0.5 flex items-center gap-1">
              <UsersRound size={9} />
              <span>{(g.members || []).length} members</span>
              {g.last_message && <span className="truncate ml-1">· {g.last_message.text?.slice(0, 24) || "📎"}</span>}
            </div>
          </div>
          <Badge n={(mentions.by_group || {})[g.group_id] || 0} testId={`group-unread-${g.group_id}`} prefix="@" className="absolute top-2 right-2" />
        </Link>
      ))}
    </>
  );
}

function HubList({ hubs, activeId, onJoin, draft, setDraft }) {
  const join = () => { const c = draft.trim(); if (c) { setDraft(""); onJoin(c); } };
  return (
    <>
      <div className="flex gap-2 mb-3">
        <Input data-testid="new-hub-city-input" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Join city…" onKeyDown={(e) => e.key === "Enter" && join()} className="bg-transparent border-b border-[#A89C98]/30 rounded-none h-9 text-sm" />
        <Button data-testid="new-hub-join" onClick={join} className="bg-[#C8A96E] text-[#15111A] hover:bg-[#A98B55] rounded-none h-9 px-3 text-xs">Join</Button>
      </div>
      {hubs.length === 0 ? (
        <div className="text-xs text-[#A89C98] px-2">No hubs yet — join a city above.</div>
      ) : hubs.map((h) => (
        <Link key={h.city} to={`/chat/hub/${encodeURIComponent(h.city)}`} data-testid={`hub-item-${h.city}`} className={`block px-3 py-2 rounded-md text-sm mb-1 ${activeId === h.city ? "bg-[#4A3356]/40 text-white" : "text-[#A89C98] hover:text-white hover:bg-white/5"}`}>
          <div className="capitalize font-heading">{h.city}</div>
          <div className="text-xs text-[#A89C98] truncate">{h.message_count} msgs</div>
        </Link>
      ))}
    </>
  );
}

function DmList({ dms, activeId }) {
  if (dms.length === 0) return <div className="text-xs text-[#A89C98] px-2">No travelers yet.</div>;
  return dms.map((d) => {
    const color = d.compat_color || "#9E8BB0";
    return (
      <Link key={d.user_id} to={`/chat/dm/${d.user_id}`} data-testid={`dm-item-${d.user_id}`} className={`flex items-center gap-3 px-3 py-2 rounded-md mb-1 border-l-2 ${activeId === d.user_id ? "bg-[#4A3356]/40" : "hover:bg-white/5"}`} style={{ borderLeftColor: d.compat_color || "transparent" }}>
        <img src={d.picture || "https://via.placeholder.com/40"} alt="" className="w-8 h-8 rounded-full border border-white/20" />
        <div className="flex-1 min-w-0">
          <div className={`text-sm truncate ${d.vip_lifetime ? "vip-name" : "text-white"}`}>{d.name}</div>
          <div className="text-[10px] text-[#A89C98] flex items-center gap-2 mt-0.5">
            <span>{ZODIAC_GLYPH[d.zodiac] || "·"} {d.personalized_tag || `${d.zodiac || "?"}-${d.chinese_zodiac || "?"}`}</span>
          </div>
          <div className="mt-1 flex items-center gap-1.5" data-testid={`dm-compat-${d.user_id}`}>
            <span className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] uppercase tracking-widest font-bold" style={{ color, border: `1px solid ${color}66`, background: `${color}14` }}>
              {d.compat_label || "Neutral"}
            </span>
            <span className="font-mono-scanner text-[11px]" style={{ color: d.compat_color || "#C8A96E" }}>{d.compatibility}%</span>
          </div>
          {d.compat_meaning && (
            <div className="mt-1 text-[10px] text-[#E8DFD3]/80 leading-snug line-clamp-2" data-testid={`dm-compat-meaning-${d.user_id}`}>{d.compat_meaning}</div>
          )}
        </div>
      </Link>
    );
  });
}

export function ChatSidebar({ className = "flex", tab, setTab, kind, id, hubs, dms, groups, mentions, onCreateGroup, onJoinHub, hubDraft, setHubDraft }) {
  const activeId = (k) => (kind === k ? id : null);
  return (
    <aside className={`glass rounded-2xl p-4 flex-col min-h-0 ${className}`} data-testid="chat-sidebar">
      <Tabs tab={tab} setTab={setTab} mentionTotal={mentions.total} />
      <div className="flex-1 overflow-y-auto min-h-0" data-testid="chat-sidebar-list">
        {tab === "groups" && <GroupList groups={groups} mentions={mentions} activeId={activeId("group")} onCreate={onCreateGroup} />}
        {tab === "hubs" && <HubList hubs={hubs} activeId={activeId("hub")} onJoin={onJoinHub} draft={hubDraft} setDraft={setHubDraft} />}
        {tab === "dms" && <DmList dms={dms} activeId={activeId("dm")} />}
      </div>
    </aside>
  );
}
